import { Fragment, useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {
  type AdminUser,
  addUserSecondaryEmail,
  listAdminUsers,
  removeUserSecondaryEmail,
  rotateUserMcpKey,
  transferAdminRole,
  unlinkUserOAuthIdentity,
  updateAdminUser,
} from '../../api/admin'
import { ApiClientError } from '../../api/client'
import { type InvitationRow, listInvitations } from '../../api/user-invitations'
import { OrgPicker, OrgProvider, useOrgContext } from '../../components/admin/OrgPicker'
import { UserClientsDrawer } from '../../components/admin/UserClientsDrawer'
import { useDialog } from '../../components/dialogs/useDialog'
import { useAuth } from '../../hooks/useAuth'
import type { UserRole } from '../../types/api'
import { InvitationTableRow } from './InvitationTableRow'
import { InvitePersonModal } from './InvitePersonModal'

const inputClassName =
  'w-full px-4 py-3 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent'

const ROLE_OPTIONS: Array<{ value: UserRole; label: string }> = [
  { value: 'admin', label: 'Admin' },
  { value: 'project_manager', label: 'Project Manager' },
  { value: 'user', label: 'User' },
]

/** Mirror privilege model A for disabled controls + tooltip (API still enforces). */
function isProtectedTarget(
  actorRole: string | undefined,
  actorId: number | undefined,
  target: AdminUser,
  kind: 'destructive' | 'self_ok'
): { blocked: boolean; reason: string } {
  if (actorId == null || !actorRole) {
    return { blocked: true, reason: 'Not authenticated' }
  }
  if (target.role === 'superadmin') {
    return { blocked: true, reason: 'Cannot modify a superadmin via user administration' }
  }
  if (actorId === target.id && kind === 'destructive') {
    return { blocked: true, reason: 'Cannot perform this action on your own account' }
  }
  if (target.role === 'admin' && actorRole !== 'superadmin' && actorId !== target.id) {
    return { blocked: true, reason: 'Only a superadmin can modify an org admin' }
  }
  return { blocked: false, reason: '' }
}

function formatLastLogin(user: AdminUser): string {
  if (!user.lastLoginAt) return 'Not yet signed in'
  const when = new Date(user.lastLoginAt)
  return when.toLocaleString()
}

function AdminUsersPageInner() {
  const dialog = useDialog()
  const { user: actor } = useAuth()
  const { selectedOrgId, isHqSuperadmin } = useOrgContext()
  const [users, setUsers] = useState<AdminUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null)
  const [rotatingUserId, setRotatingUserId] = useState<number | null>(null)
  const [emailBusyUserId, setEmailBusyUserId] = useState<number | null>(null)
  const [transferSource, setTransferSource] = useState<AdminUser | null>(null)
  const [transferTargetId, setTransferTargetId] = useState<number | ''>('')
  const [transferSourceRole, setTransferSourceRole] = useState<'project_manager' | 'user'>(
    'project_manager'
  )
  const [transferDisableSource, setTransferDisableSource] = useState(false)
  const [isTransferring, setIsTransferring] = useState(false)
  const [newSecondaryByUser, setNewSecondaryByUser] = useState<Record<number, string>>({})
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null)

  const [invitations, setInvitations] = useState<InvitationRow[]>([])
  const [query, setQuery] = useState('')
  const invitationOrgId = isHqSuperadmin ? (selectedOrgId ?? undefined) : undefined
  const loadInvitations = useCallback(async () => {
    try {
      setInvitations(
        await listInvitations(isHqSuperadmin ? (selectedOrgId ?? undefined) : undefined)
      )
    } catch {
      toast.error('Failed to load invitations')
    }
  }, [isHqSuperadmin, selectedOrgId])
  useEffect(() => {
    void loadInvitations()
  }, [loadInvitations])
  const visibleInvitations = invitations.filter(
    (invite) =>
      invite.status === 'invited' &&
      !users.some(
        (user) =>
          user.id === invite.userId ||
          (invite.email && user.email.toLowerCase() === invite.email.toLowerCase())
      ) &&
      [invite.email, invite.username, invite.githubUsername].some((value) =>
        value?.toLowerCase().includes(query.toLowerCase())
      )
  )
  const visibleUsers = users.filter((user) =>
    `${user.username} ${user.email}`.toLowerCase().includes(query.toLowerCase())
  )

  // HQ superadmin: refetch when selectedOrgId changes. Everywhere else the
  // backend scopes to the URL org, so no ?orgId= is sent.
  const loadUsers = useCallback(async () => {
    try {
      const data = await listAdminUsers(isHqSuperadmin ? (selectedOrgId ?? undefined) : undefined)
      setUsers(data)
    } catch {
      toast.error('Failed to load users')
    } finally {
      setIsLoading(false)
    }
  }, [isHqSuperadmin, selectedOrgId])

  useEffect(() => {
    void loadUsers()
  }, [loadUsers])

  const handleUpdate = async (user: AdminUser, patch: { role?: UserRole; isActive?: boolean }) => {
    setUpdatingUserId(user.id)
    try {
      const updated = await updateAdminUser(user.id, patch)
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)))
      toast.success('User updated')
    } catch (error) {
      if (error instanceof ApiClientError && error.apiError.code === 'last_admin') {
        toast.error('Cannot change the last active admin')
      } else if (error instanceof ApiClientError && error.status === 403) {
        toast.error(error.message || 'Not allowed to modify this user')
      } else {
        toast.error(error instanceof Error ? error.message : 'Failed to update user')
      }
    } finally {
      setUpdatingUserId(null)
    }
  }

  const handleRotateKey = async (user: AdminUser) => {
    const isRotate = Boolean(user.mcpKeyPrefix)
    if (isRotate) {
      const confirmed = await dialog.confirm({
        title: `Rotate ${user.username}'s MCP key?`,
        description: 'The previous key will stop working immediately.',
        confirmLabel: 'Rotate key',
        tone: 'danger',
      })
      if (!confirmed) return
    }

    setRotatingUserId(user.id)
    try {
      const rotated = await rotateUserMcpKey(user.id)
      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id
            ? {
                ...u,
                mcpKeyPrefix: rotated.tokenPrefix,
                mcpKeyLastUsedAt: null,
                mcpKeyToken: rotated.token,
              }
            : u
        )
      )
      toast.success(isRotate ? 'MCP key rotated' : 'MCP key generated')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to rotate MCP key')
    } finally {
      setRotatingUserId(null)
    }
  }

  const handleAddSecondary = async (user: AdminUser) => {
    const email = (newSecondaryByUser[user.id] ?? '').trim()
    if (!email) return
    setEmailBusyUserId(user.id)
    try {
      await addUserSecondaryEmail(user.id, email)
      setNewSecondaryByUser((prev) => ({ ...prev, [user.id]: '' }))
      toast.success('Secondary email added')
      await loadUsers()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add email')
    } finally {
      setEmailBusyUserId(null)
    }
  }

  const handleRemoveSecondary = async (user: AdminUser, emailId: number, email: string) => {
    const linked = (user.oauthIdentities ?? []).some(
      (o) => (o.providerEmail ?? '').toLowerCase() === email.toLowerCase()
    )
    const confirmed = await dialog.confirm({
      title: `Remove ${email}?`,
      description: linked
        ? 'This will also unlink OAuth identities that use this email.'
        : 'Remove this secondary email from the account.',
      confirmLabel: 'Remove',
      tone: 'danger',
    })
    if (!confirmed) return

    setEmailBusyUserId(user.id)
    try {
      await removeUserSecondaryEmail(user.id, emailId)
      toast.success('Secondary email removed')
      await loadUsers()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove email')
    } finally {
      setEmailBusyUserId(null)
    }
  }

  const handleUnlinkOAuth = async (user: AdminUser, identityId: number, provider: string) => {
    const confirmed = await dialog.confirm({
      title: `Unlink ${provider}?`,
      description:
        'The secondary email (if any) is kept. The user can re-link on next OAuth sign-in.',
      confirmLabel: 'Unlink',
      tone: 'danger',
    })
    if (!confirmed) return

    setEmailBusyUserId(user.id)
    try {
      await unlinkUserOAuthIdentity(user.id, identityId)
      toast.success(`${provider} unlinked`)
      await loadUsers()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to unlink OAuth')
    } finally {
      setEmailBusyUserId(null)
    }
  }

  const handleTransfer = async () => {
    if (!transferSource || transferTargetId === '') return
    setIsTransferring(true)
    try {
      await transferAdminRole(transferSource.id, {
        targetUserId: transferTargetId,
        sourceRole: transferSourceRole,
        disableSource: transferDisableSource,
      })
      toast.success('Admin role transferred')
      setTransferSource(null)
      await loadUsers()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to transfer admin')
    } finally {
      setIsTransferring(false)
    }
  }

  // HQ superadmin with no org selected: show empty state.
  if (isHqSuperadmin && selectedOrgId === null && !isLoading) {
    return (
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-harbor-text">People</h1>
            <p className="text-harbor-text-secondary mt-1">
              Select an organization to view its users.
            </p>
          </div>
          <OrgPicker />
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-emerald-500" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-harbor-text">People</h1>
          <p className="text-harbor-text-secondary mt-1">
            Invite your team, assign roles, and manage access in one place.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <OrgPicker />
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="px-5 py-2.5 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors shrink-0"
          >
            Invite person
          </button>
          <InvitePersonModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            orgId={invitationOrgId}
            onSuccess={loadInvitations}
          />
        </div>
      </div>

      {transferSource && (
        <div className="rounded-xl border border-amber-800/60 bg-amber-950/20 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-harbor-text">
            Transfer admin from {transferSource.username}
          </h2>
          <p className="text-sm text-harbor-text-secondary">
            Promotes the target to admin and demotes the source. Project memberships of the source
            are not changed.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="transferTarget"
                className="block text-sm font-medium text-harbor-text-secondary mb-1"
              >
                New admin
              </label>
              <select
                id="transferTarget"
                value={transferTargetId === '' ? '' : String(transferTargetId)}
                onChange={(e) => setTransferTargetId(e.target.value ? Number(e.target.value) : '')}
                className={inputClassName}
              >
                <option value="">Select user…</option>
                {users
                  .filter(
                    (u) => u.id !== transferSource.id && u.isActive && u.role !== 'superadmin'
                  )
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.username} ({u.email})
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="transferSourceRole"
                className="block text-sm font-medium text-harbor-text-secondary mb-1"
              >
                Source becomes
              </label>
              <select
                id="transferSourceRole"
                value={transferSourceRole}
                onChange={(e) =>
                  setTransferSourceRole(e.target.value as 'project_manager' | 'user')
                }
                className={inputClassName}
              >
                <option value="project_manager">Project Manager</option>
                <option value="user">User</option>
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-harbor-text-secondary">
            <input
              type="checkbox"
              checked={transferDisableSource}
              onChange={(e) => setTransferDisableSource(e.target.checked)}
            />
            Also disable the source account (revokes sessions and MCP keys)
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={isTransferring || transferTargetId === ''}
              onClick={() => void handleTransfer()}
              className="px-5 py-2.5 bg-amber-600 text-white font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
            >
              {isTransferring ? 'Transferring...' : 'Transfer admin'}
            </button>
            <button
              type="button"
              onClick={() => setTransferSource(null)}
              className="px-5 py-2.5 border border-harbor-border text-harbor-text-secondary rounded-lg hover:bg-harbor-surface-raised transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-harbor-border bg-harbor-surface shadow-sm overflow-hidden">
        <div className="p-4 flex flex-wrap items-center justify-between gap-4 border-b border-harbor-border">
          <p className="text-sm text-harbor-text-secondary">
            {users.length +
              invitations.filter(
                (i) =>
                  i.status === 'invited' &&
                  !users.some(
                    (u) =>
                      u.id === i.userId ||
                      (i.email && u.email.toLowerCase() === i.email.toLowerCase())
                  )
              ).length}{' '}
            people · One workspace
          </p>
          <div className="w-full sm:w-72">
            <label htmlFor="people-search" className="sr-only">
              Search people
            </label>
            <input
              id="people-search"
              type="search"
              placeholder="Search name or email"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className={inputClassName}
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-harbor-border">
            <thead className="bg-harbor-surface/80">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-harbor-text-secondary uppercase tracking-wider">
                  Person
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-harbor-text-secondary uppercase tracking-wider">
                  Role
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-harbor-text-secondary uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-harbor-text-secondary uppercase tracking-wider">
                  Last activity
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-harbor-text-secondary uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-harbor-border bg-harbor-surface">
              {visibleUsers.map((user) => {
                const destructive = isProtectedTarget(actor?.role, actor?.id, user, 'destructive')
                const selfOk = isProtectedTarget(actor?.role, actor?.id, user, 'self_ok')
                const secondaryCount = user.secondaryEmails?.length ?? 0

                return (
                  <Fragment key={user.id}>
                    <tr>
                      <td className="px-4 py-4">
                        <p className="text-harbor-text font-medium">{user.username}</p>
                        <p className="text-sm text-harbor-text-secondary">{user.email}</p>

                        <p className="text-xs text-harbor-text-muted mt-1">
                          Created {new Date(user.createdAt).toLocaleDateString()}
                        </p>
                      </td>
                      <td className="px-4 py-4">
                        <select
                          aria-label={`Role for ${user.username}`}
                          value={user.role}
                          disabled={updatingUserId === user.id || destructive.blocked}
                          title={destructive.blocked ? destructive.reason : undefined}
                          onChange={(e) =>
                            void handleUpdate(user, { role: e.target.value as UserRole })
                          }
                          className="min-h-11 px-3 py-2 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                        >
                          {ROLE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                            user.isActive
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                          }`}
                        >
                          {user.isActive ? 'Active' : 'Disabled'}
                        </span>
                        {user.pendingOAuthApproval && (
                          <span className="ml-2 inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-amber-900/30 text-amber-300">
                            Pending approval
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <p
                          className="text-xs text-harbor-text-secondary break-words"
                          title={formatLastLogin(user)}
                        >
                          {formatLastLogin(user)}
                        </p>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedUserId((prev) => (prev === user.id ? null : user.id))
                            }
                            className="min-h-11 min-w-11 px-2 py-1 text-xs text-harbor-text-secondary border border-harbor-border rounded hover:bg-harbor-surface-raised transition-colors"
                            title="View details"
                          >
                            View
                          </button>
                          <button
                            type="button"
                            disabled={updatingUserId === user.id || destructive.blocked}
                            title={destructive.blocked ? destructive.reason : 'User actions'}
                            onClick={() =>
                              setExpandedUserId((prev) => (prev === user.id ? null : user.id))
                            }
                            className="px-2 py-1 text-xs text-harbor-text-secondary border border-harbor-border rounded hover:bg-harbor-surface-raised disabled:opacity-50 transition-colors"
                          >
                            ⋯
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedUserId === user.id && (
                      <tr>
                        <td colSpan={5} className="px-4 py-4 bg-harbor-bg/40">
                          <div className="space-y-4">
                            <div>
                              <h3 className="text-sm font-semibold text-harbor-text mb-2">
                                Account status
                              </h3>
                              <button
                                type="button"
                                disabled={
                                  updatingUserId === user.id ||
                                  (user.isActive ? destructive.blocked : false)
                                }
                                title={
                                  user.isActive && destructive.blocked
                                    ? destructive.reason
                                    : user.isActive
                                      ? 'Disable this account'
                                      : 'Enable this account'
                                }
                                onClick={() =>
                                  void handleUpdate(user, { isActive: !user.isActive })
                                }
                                className={`min-h-11 px-3 py-2 text-sm rounded-lg border transition-colors disabled:opacity-50 ${
                                  user.isActive
                                    ? 'border-red-500/40 text-red-300 hover:bg-red-500/10'
                                    : 'border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10'
                                }`}
                              >
                                {user.isActive ? 'Disable account' : 'Enable account'}
                              </button>
                            </div>

                            <div>
                              <h3 className="text-sm font-semibold text-harbor-text mb-2">
                                Secondary emails ({secondaryCount}/5)
                              </h3>
                              <ul className="space-y-1 mb-3">
                                {(user.secondaryEmails ?? []).length === 0 && (
                                  <li className="text-xs text-harbor-text-muted">None</li>
                                )}
                                {(user.secondaryEmails ?? []).map((sec) => (
                                  <li
                                    key={sec.id}
                                    className="flex items-center justify-between gap-2 text-sm"
                                  >
                                    <span className="font-mono text-harbor-text-secondary">
                                      {sec.email}
                                      {sec.verified ? '' : ' (unverified)'}
                                    </span>
                                    <button
                                      type="button"
                                      disabled={emailBusyUserId === user.id || selfOk.blocked}
                                      title={selfOk.blocked ? selfOk.reason : undefined}
                                      onClick={() =>
                                        void handleRemoveSecondary(user, sec.id, sec.email)
                                      }
                                      className="text-xs text-red-300 hover:underline disabled:opacity-50"
                                    >
                                      Remove
                                    </button>
                                  </li>
                                ))}
                              </ul>
                              {secondaryCount < 5 && (
                                <div className="flex gap-2 max-w-md">
                                  <input
                                    type="email"
                                    placeholder="secondary@example.com"
                                    value={newSecondaryByUser[user.id] ?? ''}
                                    disabled={selfOk.blocked}
                                    title={selfOk.blocked ? selfOk.reason : undefined}
                                    onChange={(e) =>
                                      setNewSecondaryByUser((prev) => ({
                                        ...prev,
                                        [user.id]: e.target.value,
                                      }))
                                    }
                                    className={inputClassName}
                                  />
                                  <button
                                    type="button"
                                    disabled={emailBusyUserId === user.id || selfOk.blocked}
                                    title={selfOk.blocked ? selfOk.reason : undefined}
                                    onClick={() => void handleAddSecondary(user)}
                                    className="shrink-0 px-3 py-2 text-sm bg-emerald-700 text-white rounded-lg disabled:opacity-50"
                                  >
                                    Add
                                  </button>
                                </div>
                              )}
                            </div>

                            <div>
                              <h3 className="text-sm font-semibold text-harbor-text mb-2">
                                Linked OAuth
                              </h3>
                              <ul className="space-y-1">
                                {(user.oauthIdentities ?? []).length === 0 && (
                                  <li className="text-xs text-harbor-text-muted">None</li>
                                )}
                                {(user.oauthIdentities ?? []).map((ident) => (
                                  <li
                                    key={ident.id}
                                    className="flex items-center justify-between gap-2 text-sm"
                                  >
                                    <span className="text-harbor-text-secondary">
                                      {ident.provider}
                                      {ident.providerEmail ? ` · ${ident.providerEmail}` : ''}
                                    </span>
                                    <button
                                      type="button"
                                      disabled={emailBusyUserId === user.id || selfOk.blocked}
                                      title={selfOk.blocked ? selfOk.reason : undefined}
                                      onClick={() =>
                                        void handleUnlinkOAuth(user, ident.id, ident.provider)
                                      }
                                      className="text-xs text-red-300 hover:underline disabled:opacity-50"
                                    >
                                      Unlink
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <UserClientsDrawer
                              userId={user.id}
                              mcpKeyPrefix={user.mcpKeyPrefix}
                              mcpKeyLastUsedAt={user.mcpKeyLastUsedAt}
                              onRotateKey={() => void handleRotateKey(user)}
                              isRotatingKey={rotatingUserId === user.id}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
              {visibleInvitations.map((invitation) => (
                <InvitationTableRow
                  key={`invite-${invitation.id}`}
                  invitation={invitation}
                  orgId={invitationOrgId}
                  onChange={loadInvitations}
                />
              ))}
              {visibleUsers.length === 0 && visibleInvitations.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-12 text-center text-harbor-text-secondary">
                    No people found. Invite someone to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default function AdminUsersPage() {
  return (
    <OrgProvider>
      <AdminUsersPageInner />
    </OrgProvider>
  )
}
